import { create } from 'zustand';
import type { CartItem, CartSummary } from '../../types/cart';
import type { Product } from '../../types/product';
import { calculateDiscount, calculateShipping, FREE_SHIPPING_THRESHOLD } from '../../lib/utils';

interface CartState {
  items: CartItem[];
  addItem: (product: Product, variantIndex: number, quantity: number) => void;
  removeItem: (productId: string, variantIndex: number) => void;
  updateQuantity: (productId: string, variantIndex: number, quantity: number) => void;
  clearCart: () => void;
  getSummary: () => CartSummary;
}

export const useCartStore = create<CartState>((set, get) => ({
  items: [],

  addItem: (product, variantIndex, quantity) => {
    set((state) => {
      const existingIndex = state.items.findIndex(
        (item) => item.product_id === product.id && item.variant_index === variantIndex
      );

      if (existingIndex > -1) {
        const updatedItems = [...state.items];
        updatedItems[existingIndex] = {
          ...updatedItems[existingIndex],
          quantity: updatedItems[existingIndex].quantity + quantity,
          updated_at: new Date().toISOString(),
        };
        return { items: updatedItems };
      }

      const newItem: CartItem = {
        id: `${product.id}-${variantIndex}`,
        user_id: '', // Va fi setat dacă este autentificat
        product_id: product.id,
        variant_index: variantIndex,
        quantity,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        product,
      };

      return { items: [...state.items, newItem] };
    });
  },

  removeItem: (productId, variantIndex) => {
    set((state) => ({
      items: state.items.filter(
        (item) => !(item.product_id === productId && item.variant_index === variantIndex)
      ),
    }));
  },

  updateQuantity: (productId, variantIndex, quantity) => {
    set((state) => ({
      items: state.items.map((item) =>
        item.product_id === productId && item.variant_index === variantIndex
          ? { ...item, quantity, updated_at: new Date().toISOString() }
          : item
      ),
    }));
  },

  clearCart: () => set({ items: [] }),

  getSummary: () => {
    const { items } = get();
    let rawSubtotal = 0;
    let totalDiscount = 0;
    let itemCount = 0;

    items.forEach((item) => {
      const product = item.product;
      if (!product) return;

      const variant = product.variants[item.variant_index];
      if (!variant) return;

      const price = variant.price;
      const qty = item.quantity;
      itemCount += qty;

      const itemSubtotal = price * qty;
      rawSubtotal += itemSubtotal;

      // Calcul discount DDS
      const componentsCount = product.metafields?.bundle_components?.length || 1;
      const discountPercent = calculateDiscount(product.is_bundle, componentsCount, qty);
      const discountForItem = Math.round(itemSubtotal * (discountPercent / 100));
      totalDiscount += discountForItem;
    });

    const netSubtotal = rawSubtotal - totalDiscount;
    const shippingCost = items.length === 0 ? 0 : calculateShipping(netSubtotal);
    const total = netSubtotal + shippingCost;
    const amountUntilFreeShipping = Math.max(0, FREE_SHIPPING_THRESHOLD - netSubtotal);

    return {
      items,
      subtotal: netSubtotal, // În tipul CartSummary, subtotal-ul este net (după discount)
      shippingCost,
      discountAmount: totalDiscount,
      total,
      itemCount,
      freeShippingThreshold: FREE_SHIPPING_THRESHOLD,
      amountUntilFreeShipping,
    };
  },
}));
